import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MobileLayout from "../../components/layout/MobileLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import ConfirmBidsModal from "../../components/ConfirmBidsModal";
import BetWarningModal from "../../components/BetWarningModal";
import { BottomBar, QuickChipStrip } from "./JodiBetPage";

const QUICK_AMOUNTS = [50, 100, 200, 500, 1000];
const DIGITS = Array.from({ length: 10 }, (_, i) => i);

export default function HarufPage({ embedded = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [market, setMarket] = useState(null);
  const [settings, setSettings] = useState({});
  const [bets, setBets] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quickAmt, setQuickAmt] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [warn, setWarn] = useState(null);

  useEffect(() => {
    api.get(`/markets/${id}`).then(m => setMarket(m.data));
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [id]);

  const total = useMemo(() => Object.values(bets).reduce((s, v) => s + (Number(v) || 0), 0), [bets]);
  const minBet = settings.min_bet_haruf ?? 100;
  const keyFor = (sd, d) => `${sd}-${d}`;

  function setAmt(sd, d, val) {
    const raw = String(val).replace(/\D/g, "").slice(0, 6);
    const n = Number(raw) || 0;
    const k = keyFor(sd, d);
    setBets(prev => { const next = { ...prev }; if (n > 0) next[k] = n; else delete next[k]; return next; });
  }
  function tapTile(sd, d) {
    if (quickAmt > 0) {
      const k = keyFor(sd, d);
      setBets(prev => ({ ...prev, [k]: (Number(prev[k]) || 0) + quickAmt }));
    }
  }
  function clearKey(k) {
    setBets(prev => { const next = { ...prev }; delete next[k]; return next; });
  }

  function openReview() {
    if (!market?.is_market_active) {
      setWarn({ type: "closed", title: "मार्केट बंद है", message: "अभी इस मार्केट पर बोली नहीं लगाई जा सकती।" });
      return;
    }
    if (total <= 0) {
      setWarn({ type: "empty", title: "कोई हरुफ चुनें", message: "अंदर या बाहर हरुफ में किसी digit के सामने amount डालें।" });
      return;
    }
    if (total < minBet) {
      setWarn({ type: "min", title: "अमाउंट बहुत कम है", message: `हरुफ के लिए मिनिमम बोली ₹${minBet} है।`, hint: `अभी आपका टोटल ₹${total} है` });
      return;
    }
    if (total > (user?.wallet_balance || 0)) {
      setWarn({ type: "insufficient", title: "वॉलेट में बैलेंस कम है", message: `वॉलेट में सिर्फ़ ₹${Number(user?.wallet_balance || 0).toFixed(2)} है।`, hint: `बोली का टोटल ₹${total}` });
      return;
    }
    setShowReview(true);
  }

  async function confirmPlace() {
    const payload = {
      market_id: market.id,
      bids: Object.entries(bets).filter(([_, v]) => Number(v) > 0).map(([k, amt]) => {
        const [sd, d] = k.split("-");
        // sd = "A" (अंदर/Andar) → haruf_andar, sd = "B" (बाहर/Bahar) → haruf_bahar
        return { game_type: sd === "A" ? "haruf_andar" : "haruf_bahar", number: d, amount: Number(amt) };
      }),
    };
    try {
      setSubmitting(true);
      await api.post("/bids", payload);
      await refresh();
      setShowReview(false);
      toast.success(`✅ ${payload.bids.length} हरुफ बोली लगी (कुल ₹${total})`);
      navigate("/my-bids");
    } catch (e) { toast.error(e?.response?.data?.detail || "बोली नहीं लगी"); }
    finally { setSubmitting(false); }
  }

  const rows = Object.entries(bets).sort().map(([k, amt]) => {
    const [sd, d] = k.split("-");
    return { key: k, label: `हरुफ: ${sd === "A" ? "अंदर" : "बाहर"} ${d}`, amount: Number(amt) };
  });

  return (
    <Wrap embedded={embedded}>
      <QuickChipStrip
        value={quickAmt}
        onChange={setQuickAmt}
        presets={QUICK_AMOUNTS}
        testIdPrefix="haruf"
        hint="चिप दबाकर टाइल पर टैप करें • या सीधे हर digit के सामने amount लिखें"
      />

      {/* ===== अंदर हरुफ ===== */}
      <SectionHeader color="cyan" icon={<ArrowDownToLine className="w-4 h-4" />} title="अंदर हरुफ" subtitle="ANDAR" />
      <DigitRow side="A" bets={bets} onAmt={setAmt} onTap={tapTile} />

      <div className="h-3" />

      {/* ===== बाहर हरुफ ===== */}
      <SectionHeader color="amber" icon={<ArrowUpFromLine className="w-4 h-4" />} title="बाहर हरुफ" subtitle="BAHAR" />
      <DigitRow side="B" bets={bets} onAmt={setAmt} onTap={tapTile} />

      <div className="h-28" />

      <BottomBar
        label="कुल पॉइंट्स"
        value={total}
        onProceed={openReview}
        canProceed={total > 0 && market?.is_market_active}
      />

      <ConfirmBidsModal
        open={showReview}
        rows={rows}
        total={total}
        onClose={() => setShowReview(false)}
        onConfirm={confirmPlace}
        onDelete={clearKey}
        submitting={submitting}
      />

      <BetWarningModal
        open={!!warn}
        type={warn?.type}
        title={warn?.title}
        message={warn?.message}
        hint={warn?.hint}
        onClose={() => setWarn(null)}
      />
    </Wrap>
  );
}

function SectionHeader({ color, icon, title, subtitle }) {
  const palette = color === "amber"
    ? { ring: "border-amber-400/50", bg: "bg-amber-400/5", text: "text-amber-200", glow: "shadow-[0_0_18px_rgba(251,191,36,0.25)]", pill: "bg-amber-400/15 border-amber-400/40 text-amber-300" }
    : { ring: "border-cyan-400/50", bg: "bg-cyan-400/5", text: "text-cyan-200", glow: "shadow-[0_0_18px_rgba(56,189,248,0.25)]", pill: "bg-cyan-400/15 border-cyan-400/40 text-cyan-300" };
  return (
    <div
      className={`rounded-xl border ${palette.ring} ${palette.bg} ${palette.glow} px-3 py-2 mb-2 flex items-center justify-between`}
      data-testid={`haruf-section-${subtitle.toLowerCase()}`}
    >
      <div className={`font-display font-black ${palette.text} text-base tracking-wide inline-flex items-center gap-2`}>
        {icon}
        {title}
      </div>
      <span className={`text-[10px] font-black tracking-[0.25em] px-2 py-0.5 rounded-full border ${palette.pill}`}>
        {subtitle}
      </span>
    </div>
  );
}

function DigitRow({ side, bets, onAmt, onTap }) {
  return (
    <div className="grid grid-cols-5 gap-2" data-testid={`haruf-grid-${side}`}>
      {DIGITS.map(d => {
        const k = `${side}-${d}`;
        const amt = bets[k] || 0;
        const active = amt > 0;
        return (
          <div
            key={d}
            onClick={() => onTap(side, d)}
            data-testid={`haruf-digit-${side}-${d}`}
            className={`relative rounded-xl p-2 flex flex-col items-center transition ${
              active
                ? "border-2 border-cyan-400 bg-cyan-400/5 shadow-[0_0_14px_rgba(56,189,248,0.45)]"
                : "border border-cyan-400/15 bg-black/40"
            }`}
          >
            <span className={`font-display font-black text-base leading-tight ${active ? "text-cyan-200" : "text-white"}`}>
              {side}-{d}
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={amt > 0 ? String(amt) : ""}
              onChange={(e) => onAmt(side, d, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="₹"
              data-testid={`haruf-amt-${side}-${d}`}
              className={`mt-1.5 w-full h-7 rounded-md text-center text-sm font-black tabular-nums bg-transparent focus:outline-none placeholder:text-slate-600 ${
                active ? "border border-cyan-400/60 text-cyan-200 bg-cyan-400/5" : "border border-slate-700 text-slate-300"
              }`}
            />
          </div>
        );
      })}
    </div>
  );
}

function Wrap({ embedded, children }) {
  if (embedded) return <>{children}</>;
  return <MobileLayout hideBottomNav hideHeader><div className="px-4 pt-4">{children}</div></MobileLayout>;
}
